import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../../../../hooks/redux';
import { getLiveGuidelineRecentUpdatesAction } from '../../../../../../redux/actions/liveGuidelineActions';
import {
    LiveGuidelineActivity,
    LiveGuidelineRecentUpdates,
} from '../../../../../../redux/actions/liveGuidelineActions/types';
import { ActionResponse } from '../../../../../../models/actionResponse/types';
import { ExternalProduct } from '../../../../../../models/product/types';
import { AppDispatch } from '../../../../../../redux/store';
import { getExternalProductsAction } from '../../../../../../redux/actions/transparencyCatalogActions';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../../../../../../redux/actions/transparencyCatalogActions/types';
import { flattenTcMfSectionProducts } from '../../../../../../helpers/tcExternalProducts';
import { chunkArray } from '../../../../../../helpers/array';
import Switcher from '../../../../../../components/common/switcher/Switcher';
import { blueDark02 } from '../../../../../../styles/colors';
import { toShortLocalDate } from '../../../../../../helpers/dateHelper';
import {
    BannerCloseButton,
    BannerContainer,
    BannerSubtext,
    BannerTitle,
    BannerToggleTrigger,
    BannerWrapper,
    BoltIcon,
    MfrGroup,
    MfrName,
    NoneText,
    NotificationContainer,
    ProductLink,
    ProductsGrid,
    SectionLabel,
} from './LiveGuidelineNotificationBanner.styles';
import { Link } from 'react-router-dom';

interface Props {
    liveGuidelineId: number;
    onNavigateToNotifications: () => void;
}

const EXTERNAL_PRODUCTS_CHUNK_SIZE = 700;
const UNKNOWN_MANUFACTURER = 'Unknown manufacturer';
const SEVEN_DAYS_IN_MS = 7 * 24 * 60 * 60 * 1000;

interface ManufacturerGroup {
    manufacturer: string;
    products: Array<{ id: number; name: string; url?: string }>;
}

const loadExternalProducts = async (
    dispatch: AppDispatch,
    productIds: number[],
): Promise<Map<number, ExternalProduct>> => {
    const results = await Promise.all(
        chunkArray(productIds, EXTERNAL_PRODUCTS_CHUNK_SIZE).map((chunk) =>
            dispatch(getExternalProductsAction({ productIds: chunk })),
        ),
    );
    const products = results.flatMap((result) => {
        const response = result.payload as
            | { data?: GetProductsFromTransparencyCatalogAPIResponse }
            | undefined;
        return response?.data?.mfSection ? flattenTcMfSectionProducts(response.data.mfSection) : [];
    });
    return new Map(products.map((product) => [product.id, product]));
};

const groupByManufacturer = (
    activities: LiveGuidelineActivity[],
    productsById: Map<number, ExternalProduct>,
): ManufacturerGroup[] => {
    const groups = new Map<string, Map<number, { name: string; url?: string }>>();
    activities.forEach((activity) => {
        if (!activity.productId) return;
        const product = productsById.get(activity.productId);
        const manufacturer = product?.company || UNKNOWN_MANUFACTURER;
        const group = groups.get(manufacturer) ?? new Map();
        group.set(activity.productId, {
            name: product?.productName ?? `Product ${activity.productId}`,
            url: product?.transparencyCatalogUrl,
        });
        groups.set(manufacturer, group);
    });
    return [...groups.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([manufacturer, products]) => ({
            manufacturer,
            products: [...products.entries()]
                .map(([id, product]) => ({ id, ...product }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        }));
};

const LiveGuidelineNotificationBanner: React.FC<Props> = ({
    liveGuidelineId,
    onNavigateToNotifications,
}) => {
    const dispatch = useAppDispatch();

    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [recentUpdates, setRecentUpdates] = useState<LiveGuidelineRecentUpdates | null>(null);
    const [sinceDate, setSinceDate] = useState<string | null>(null);
    const [productsById, setProductsById] = useState<Map<number, ExternalProduct>>(new Map());

    useEffect(() => {
        loadRecentUpdates();
    }, [liveGuidelineId]);

    const loadRecentUpdates = async () => {
        const { payload } = (await dispatch(
            getLiveGuidelineRecentUpdatesAction(liveGuidelineId),
        )) as ActionResponse<LiveGuidelineRecentUpdates>;
        if (payload?.data) {
            setRecentUpdates(payload.data);
            const dates = payload.data.added
                .map((a) => a.createdOn)
                .filter(Boolean)
                .sort();
            setSinceDate(dates[0] ?? null);

            const productIds = [
                ...new Set(
                    [...payload.data.added, ...payload.data.removed, ...payload.data.hidden]
                        .map((a) => a.productId)
                        .filter((id): id is number => Boolean(id)),
                ),
            ];
            if (productIds.length) {
                setProductsById(await loadExternalProducts(dispatch, productIds));
            }
        }
    };

    const now = Date.now();
    const hasRecentUpdates =
        recentUpdates !== null &&
        [...recentUpdates.added, ...recentUpdates.removed, ...recentUpdates.hidden].some(
            ({ createdOn }) => {
                const age = now - new Date(createdOn).getTime();
                return age >= 0 && age <= SEVEN_DAYS_IN_MS;
            },
        );

    if (dismissed || !hasRecentUpdates) return null;

    const renderProductGroup = (activities: LiveGuidelineActivity[]) => {
        const groups = groupByManufacturer(activities, productsById);
        if (!groups.length) return <NoneText>None</NoneText>;
        return (
            <ProductsGrid>
                {groups.map(({ manufacturer, products }) => (
                    <MfrGroup key={manufacturer}>
                        <MfrName>{manufacturer}</MfrName>
                        {products.map((product) =>
                            product.url ? (
                                <ProductLink
                                    key={product.id}
                                    href={product.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {product.name}
                                </ProductLink>
                            ) : (
                                <NoneText key={product.id}>{product.name}</NoneText>
                            ),
                        )}
                    </MfrGroup>
                ))}
            </ProductsGrid>
        );
    };

    return (
        <BannerWrapper>
            <BannerContainer>
                <BannerToggleTrigger onClick={() => setOpen((prev) => !prev)}>
                    <Switcher
                        width={20}
                        height={20}
                        clicked={open}
                        stopPropagation={false}
                        iconColor={blueDark02}
                        iconColorActive={'#ffd788'}
                        bgColorActive={blueDark02}
                        bgColor={'transparent'}
                    />
                    <BannerTitle>
                        <BoltIcon src="src/assets/images/lightning-charge-fill.svg" />
                        Updates since {sinceDate ? toShortLocalDate(sinceDate) : 'Mon DD, YYYY'}
                    </BannerTitle>
                </BannerToggleTrigger>

                <BannerCloseButton onClick={() => setDismissed(true)} aria-label="Close">
                    ✕
                </BannerCloseButton>

                {open && (
                    <NotificationContainer>
                        <BannerSubtext>
                            See the{' '}
                            <Link
                                to={''}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onNavigateToNotifications();
                                }}
                            >
                                Notifications tab
                            </Link>{' '}
                            for all updates to this Live Guideline since it was created.
                        </BannerSubtext>

                        <SectionLabel isFirst={true}>Products added</SectionLabel>
                        {renderProductGroup(recentUpdates?.added ?? [])}

                        <SectionLabel>Products removed</SectionLabel>
                        {renderProductGroup(recentUpdates?.removed ?? [])}

                        <SectionLabel>Products hidden</SectionLabel>
                        {renderProductGroup(recentUpdates?.hidden ?? [])}
                    </NotificationContainer>
                )}
            </BannerContainer>
        </BannerWrapper>
    );
};

export default LiveGuidelineNotificationBanner;
